import React, { useState, useEffect } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import useAcademy from "../../../../hooks/useAcademy";

export default function VideoModal({
  setOpenVideoModal,
  trainingModuleId,
  onTrigger,
  video,
}) {
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    videoUrl: "",
    description: "",
    image: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { saveTrainingVideo } = useAcademy();

  useEffect(() => {
    if (video) {
      setFormData({
        title: video.title || "",
        videoUrl: video.video || "",
        description: video.description || "",
        image: video.thumbnail || null,
      });
      if (video.thumbnail) {
        setPreview(video.thumbnail);
      }
    }
  }, [video]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Training Title is required";

    if (!formData.videoUrl.trim()) {
      newErrors.videoUrl = "Video URL is required";
    } else {
      try {
        new URL(formData.videoUrl);
      } catch (err) {
        newErrors.videoUrl = "Please enter a valid URL";
      }
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.image && !video) {
      newErrors.image = "Image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, image: file }));
      setErrors((prev) => ({ ...prev, image: "" }));
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const payload = new FormData();
    payload.append("title", formData.title);
    payload.append("description", formData.description);
    payload.append("video", formData.videoUrl);
    if (formData.image instanceof File) {
      payload.append("thumbnail", formData.image);
    }
    payload.append("trainingModule", trainingModuleId);

    try {
      if (video) {
        console.log("vdieo here")
        // Update
        await saveTrainingVideo(setLoading, payload, video._id, "PATCH");
      } else {
        // Create
        console.log("vdieo not here")
        await saveTrainingVideo(setLoading, payload);
      }
      void onTrigger();
      setOpenVideoModal(false);
    } catch (err) {
      console.error("Error submitting form:", err);
      setErrors({ submit: "Failed to submit form. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white w-full max-w-xl relative max-h-[94vh] overflow-y-auto rounded-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center bg-blue-600 text-white px-6 py-4 sticky top-0">
          <h2 className="text-lg font-semibold text-white">
            {video ? "Update Training Video" : "Create a new Training Video"}
          </h2>
          <CloseIcon
            onClick={() => setOpenVideoModal(false)}
            className="cursor-pointer"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 px-6 py-3">
          <div className="flex flex-col">
            <p className="text-sm">Training Title</p>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="eg. Starter"
              className={`w-full border ${
                errors["title"] ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            />
            {errors["title"] && (
              <p className="text-red-500 text-xs mt-1">{errors["title"]}</p>
            )}
          </div>

          <div className="flex flex-col">
            <p className="text-sm">Video Url</p>
            <input
              type="text"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleInputChange}
              placeholder="eg. https://youtube.com/..."
              className={`w-full border ${
                errors["videoUrl"] ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            />
            {errors["videoUrl"] && (
              <p className="text-red-500 text-xs mt-1">{errors["videoUrl"]}</p>
            )}
          </div>

          <div className="flex flex-col">
            <p className="text-sm">Short Description</p>
            <textarea
              rows={2}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="eg. Starter"
              className={`w-full border ${
                errors["description"] ? "border-red-500" : "border-gray-300"
              } rounded-md px-4 py-2 focus:outline-none`}
            />
            {errors["description"] && (
              <p className="text-red-500 text-xs mt-1">
                {errors["description"]}
              </p>
            )}
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            className={`border ${
              errors["image"]
                ? "border-red-500"
                : "border-dashed border-gray-300"
            } rounded-lg flex flex-col items-center justify-center p-6 cursor-pointer hover:bg-gray-50`}
          >
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {preview ? (
              <div className="w-full flex items-center space-y-2">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-14 rounded-md object-cover"
                />
                <label
                  htmlFor="image-upload"
                  className="text-blue-600 hover:underline text-sm cursor-pointer"
                >
                  Change Image
                </label>
              </div>
            ) : (
              <label
                htmlFor="image-upload"
                className="flex flex-col items-center justify-center w-full h-full text-gray-400"
              >
                <CloudUploadIcon />
                <p className="text-sm">Drag & drop or click to upload image</p>
              </label>
            )}
            {errors["image"] && (
              <p className="text-red-500 text-xs mt-1">{errors["image"]}</p>
            )}
          </div>

          {errors["submit"] && (
            <p className="text-red-500 text-sm text-center">
              {errors["submit"]}
            </p>
          )}

          <div className="grid grid-cols-4 gap-6 mt-4">
            <button
              type="button"
              onClick={() => setOpenVideoModal(false)}
              className="bg-white border font-medium border-gray-100 rounded-md text-gray-700 col-span-1 py-3 flex justify-center items-center hover:bg-gray-100"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-theme1 rounded-md font-bold text-white py-3 col-span-3 flex justify-center items-center hover:bg-blue-700"
            >
              {loading
                ? video
                  ? "Updating..."
                  : "Creating..."
                : video
                ? "Update"
                : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
